import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { PluginState, PluginActions } from '../types/store';
import { LogType } from '../types';
import { ProgressEntry } from '../types/logs';
import { SchemaVariant } from '@morph-mapper/types';
import { getKeys } from '@morph-mapper/utils';

export const useGlobalPluginStore = create<PluginState & PluginActions>()(
  immer((set, get) => ({
    plugins: [],
    versions: {},
    settings: {},
    bootstrapped: {},
    logs: [],
    progress: {},
    lastFile: '',
    skipSameLastFile: false,
    domain: '',
    fileType: SchemaVariant.Pdf,
    textContent: '',
    fileIndex: 0,
    totalFiles: 0,

    addPlugin: (plugin, version, settings) => {
      set((s) => {
        if (!s.plugins.includes(plugin)) {
          s.plugins.push(plugin);
        }
        s.versions[plugin] = version;
        s.settings[plugin] = settings;
      });
    },
    clearPlugins: () => {
      set((s) => {
        s.plugins = [];
        s.versions = {};
      });
    },
    setSettings: (plugin, settings) => {
      set((s) => {
        s.settings[plugin] = settings;
      });
    },
    getPlugins: () => {
      return get().plugins;
    },
    getPluginVersion: (plugin) => {
      const version = get().versions[plugin];
      // TODO: We do not want to crash the entire application due to a plugin, but this is a serious error.
      // We probably want to save any state we can and disable the plugin instead, notifying the user/developer about the fault.
      if (!version) {
        throw new Error(`Plugin “${plugin}” is not registered.`);
      }

      return version;
    },
    setBootstrapped: (plugin, value) => {
      set((s) => {
        s.bootstrapped[plugin] = value;
      });
    },
    getBootstrapped: (plugin) => {
      const bootstrapped = get().bootstrapped[plugin];
      // TODO: Same as above, we do not want to crash the entire application due to a plugin.
      if (bootstrapped === undefined) {
        throw new Error(`Plugin “${plugin}” is not registered.`);
      }

      return bootstrapped;
    },
    resetAllBootstrapped: () => {
      set((s) => {
        for (const plugin of getKeys(s.bootstrapped)) {
          s.bootstrapped[plugin] = false;
        }
      });
    },
    setGlobalState: (state) => {
      set((s) => {
        s.plugins = state.plugins;
        s.versions = state.versions;
        s.settings = state.settings;
        s.bootstrapped = state.bootstrapped;
        s.lastFile = state.lastFile;
        s.skipSameLastFile = state.skipSameLastFile;
        s.domain = state.domain;
        s.fileType = state.fileType;
        s.textContent = state.textContent;
      });
    },
    getGlobalState: () => ({
      plugins: get().plugins,
      versions: get().versions,
      settings: get().settings,
      bootstrapped: get().bootstrapped,
      logs: get().logs,
      progress: get().progress,
      lastFile: get().lastFile,
      skipSameLastFile: get().skipSameLastFile,
      domain: get().domain,
      fileType: get().fileType,
      textContent: get().textContent,
      fileIndex: get().fileIndex,
      totalFiles: get().totalFiles,
    }),

    getFiles: () => {
      return get().lastFile;
    },
    setFile: (file) => {
      set((s) => {
        s.lastFile = file;
      });
    },
    setSkipFile: (state) => {
      set((s) => {
        if (state === false) {
          s.skipSameLastFile = false;
        } else {
          s.skipSameLastFile = true;
        }
      });
    },
    getSkipFile: () => {
      return get().skipSameLastFile;
    },

    log: (plugin, message, type = LogType.Info) => {
      set((s) => {
        s.logs.push({ plugin, message, type: type, time: Date.now() });
      });
    },
    clearLogs: () => {
      set((s) => {
        s.logs = [];
      });
    },
    setProgress: (plugin, value, message) => {
      set((s) => {
        s.progress[plugin] = { plugin, value, message, time: Date.now() } as ProgressEntry;
      });
    },
    resetProgress: (plugin) => {
      set((s) => {
        if (plugin) {
          delete s.progress[plugin];
        } else {
          s.progress = {};
        }
      });
    },
    setTextContent: (textContent) => {
      set((s) => {
        s.textContent = textContent;
      });
    },
    setMultiFileContext: (fileIndex, totalFiles) => {
      set((s) => {
        s.fileIndex = fileIndex;
        s.totalFiles = totalFiles;
      });
    },
  }))
);

if (typeof window !== 'undefined') (window as any).__ps = useGlobalPluginStore;
